import React, { useState, Component } from 'react';
import { motion } from 'framer-motion';
import { ShieldCheckIcon, AlertTriangleIcon, XCircleIcon, TrendingUpIcon, ClockIcon, PlaneIcon, CheckCircleIcon, DollarSignIcon, UsersIcon, FileTextIcon, ArrowRightIcon } from 'lucide-react';
import { NIGERIAN_AIRLINES } from '../../constants/data';
import { formatNaira } from '../../utils/formatCurrency';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Modal, ModalFooter } from '../../components/ui/Modal';
import { MetricGrid } from '../../components/features/MetricCard';
import { StatusBadge } from '../../components/ui/StatusBadge';
// Airline Compliance Card Component
function AirlineComplianceCard({
  airline,
  onClick
}: {
  airline: (typeof NIGERIAN_AIRLINES)[0];
  onClick: () => void;
}) {
  const getStatusColor = (score: number) => {
    if (score >= 90) return 'text-success-600 bg-success-50 border-success-200';
    if (score >= 70) return 'text-warning-600 bg-warning-50 border-warning-200';
    return 'text-error-600 bg-error-50 border-error-200';
  };
  const getStatusLabel = (score: number) => {
    if (score >= 90) return 'Compliant';
    if (score >= 70) return 'Warning';
    return 'Non-Compliant';
  };
  return <motion.div whileHover={{
    scale: 1.02
  }} whileTap={{
    scale: 0.98
  }} onClick={onClick} className="cursor-pointer h-full" data-id="element-1511">
      <Card variant="elevated" padding="lg" className="h-full border border-surface-200 hover:border-primary-200 transition-colors" data-id="element-1512">
        <div className="flex items-start justify-between mb-6" data-id="element-1513">
          <div className="flex items-center gap-3" data-id="element-1514">
            <div className="w-12 h-12 bg-surface-100 rounded-xl flex items-center justify-center p-2" data-id="element-1515">
              <img src={airline.logo} alt={airline.name} className="w-full h-full object-contain" data-id="element-1516" />
            </div>
            <div data-id="element-1517">
              <h3 className="text-heading-sm font-bold text-surface-900" data-id="element-1518">
                {airline.name}
              </h3>
              <p className="text-caption text-surface-500" data-id="element-1519">{airline.code}</p>
            </div>
          </div>
          <span className={`px-2.5 py-1 rounded-full text-caption font-bold border ${getStatusColor(airline.complianceScore)}`} data-id="element-1520">
            {getStatusLabel(airline.complianceScore)}
          </span>
        </div>

        <div className="space-y-4" data-id="element-1521">
          <div data-id="element-1522">
            <div className="flex justify-between text-body-sm mb-1" data-id="element-1523">
              <span className="text-surface-600" data-id="element-1524">Compliance Score</span>
              <span className="font-bold text-surface-900" data-id="element-1525">
                {airline.complianceScore}%
              </span>
            </div>
            <div className="h-2 bg-surface-100 rounded-full overflow-hidden" data-id="element-1526">
              <motion.div initial={{
              width: 0
            }} animate={{
              width: `${airline.complianceScore}%`
            }} transition={{
              duration: 1,
              ease: 'easeOut'
            }} className={`h-full rounded-full ${airline.complianceScore >= 90 ? 'bg-success-500' : airline.complianceScore >= 70 ? 'bg-warning-500' : 'bg-error-500'}`} data-id="element-1527" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 pt-4 border-t border-surface-100" data-id="element-1528">
            <div data-id="element-1529">
              <p className="text-caption text-surface-500 mb-1" data-id="element-1530">
                Avg. Refund Time
              </p>
              <div className="flex items-center gap-1.5" data-id="element-1531">
                <ClockIcon className="w-3.5 h-3.5 text-surface-400" data-id="element-1532" />
                <span className="text-body-sm font-semibold text-surface-900" data-id="element-1533">
                  {airline.avgRefundTime} min
                </span>
              </div>
            </div>
            <div data-id="element-1534">
              <p className="text-caption text-surface-500 mb-1" data-id="element-1535">
                Total Refunds
              </p>
              <div className="flex items-center gap-1.5" data-id="element-1536">
                <FileTextIcon className="w-3.5 h-3.5 text-surface-400" data-id="element-1537" />
                <span className="text-body-sm font-semibold text-surface-900" data-id="element-1538">
                  {airline.totalRefunds.toLocaleString()}
                </span>
              </div>
            </div>
          </div>
        </div>
      </Card>
    </motion.div>;
}
export function ComplianceMonitor() {
  const [selectedAirline, setSelectedAirline] = useState<(typeof NIGERIAN_AIRLINES)[0] | null>(null);
  // Calculate overall system compliance
  const avgCompliance = Math.round(NIGERIAN_AIRLINES.reduce((acc, curr) => acc + curr.complianceScore, 0) / NIGERIAN_AIRLINES.length);
  const metrics = [{
    label: 'System Compliance',
    value: `${avgCompliance}%`,
    change: {
      value: 2.5,
      trend: 'up' as const
    },
    icon: <ShieldCheckIcon className="w-5 h-5" data-id="element-1539" />,
    iconColor: 'bg-success-100 text-success-600'
  }, {
    label: 'Compliant Airlines',
    value: NIGERIAN_AIRLINES.filter(a => a.complianceScore >= 90).length,
    icon: <CheckCircleIcon className="w-5 h-5" data-id="element-1540" />,
    iconColor: 'bg-primary-100 text-primary-600'
  }, {
    label: 'At Risk',
    value: NIGERIAN_AIRLINES.filter(a => a.complianceScore >= 70 && a.complianceScore < 90).length,
    icon: <AlertTriangleIcon className="w-5 h-5" data-id="element-1541" />,
    iconColor: 'bg-warning-100 text-warning-600'
  }, {
    label: 'Non-Compliant',
    value: NIGERIAN_AIRLINES.filter(a => a.complianceScore < 70).length,
    icon: <XCircleIcon className="w-5 h-5" data-id="element-1542" />,
    iconColor: 'bg-error-100 text-error-600'
  }];
  return <div className="space-y-8" data-id="element-1543">
      {/* Header Metrics */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} data-id="element-1544">
        <div className="mb-8" data-id="element-1545">
          <h1 className="text-heading-lg font-bold text-surface-900 mb-2" data-id="element-1546">
            Overall System Compliance
          </h1>
          <p className="text-body-md text-surface-600 max-w-3xl" data-id="element-1547">
            Based on refund processing times, success rates, and regulatory
            adherence across all monitored airlines.
          </p>
        </div>
        <MetricGrid metrics={metrics} columns={4} data-id="element-1548" />
      </motion.div>

      {/* Airlines Grid */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.2
    }} data-id="element-1549">
        <h2 className="text-heading-md font-bold text-surface-900 mb-6" data-id="element-1550">
          Monitored Airlines
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-1551">
          {NIGERIAN_AIRLINES.map((airline, index) => <motion.div key={airline.id} initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.2 + index * 0.1
        }} data-id="element-1552">
              <AirlineComplianceCard airline={airline} onClick={() => setSelectedAirline(airline)} data-id="element-1553" />
            </motion.div>)}
        </div>
      </motion.div>

      {/* Airline Detail Modal */}
      <Modal isOpen={!!selectedAirline} onClose={() => setSelectedAirline(null)} title={selectedAirline?.name} size="lg" data-id="element-1554">
        {selectedAirline && <div className="space-y-8" data-id="element-1555">
            {/* Header Stats */}
            <div className="flex items-center gap-4 p-4 bg-surface-50 rounded-xl border border-surface-200" data-id="element-1556">
              <div className="w-16 h-16 bg-white rounded-xl flex items-center justify-center p-2 shadow-sm border border-surface-100" data-id="element-1557">
                <img src={selectedAirline.logo} alt={selectedAirline.name} className="w-full h-full object-contain" data-id="element-1558" />
              </div>
              <div data-id="element-1559">
                <div className="flex items-center gap-3 mb-1" data-id="element-1560">
                  <h3 className="text-heading-md font-bold text-surface-900" data-id="element-1561">
                    {selectedAirline.name}
                  </h3>
                  <span className="px-2 py-0.5 bg-surface-200 text-surface-700 text-caption font-bold rounded-md" data-id="element-1562">
                    {selectedAirline.code}
                  </span>
                </div>
                <div className="flex items-center gap-4 text-body-sm text-surface-600" data-id="element-1563">
                  <span className="flex items-center gap-1.5" data-id="element-1564">
                    <ShieldCheckIcon className="w-4 h-4 text-primary-500" data-id="element-1565" />
                    Score: {selectedAirline.complianceScore}%
                  </span>
                  <span className="flex items-center gap-1.5" data-id="element-1566">
                    <ClockIcon className="w-4 h-4 text-primary-500" data-id="element-1567" />
                    Avg Time: {selectedAirline.avgRefundTime} min
                  </span>
                </div>
              </div>
            </div>

            {/* Detailed Metrics Grid */}
            <div className="grid grid-cols-2 gap-4" data-id="element-1568">
              {/* No Shows */}
              <div className="p-4 bg-white border border-surface-200 rounded-xl shadow-sm" data-id="element-1569">
                <div className="flex items-center gap-2 mb-2" data-id="element-1570">
                  <div className="p-1.5 bg-warning-100 rounded-lg" data-id="element-1571">
                    <UsersIcon className="w-4 h-4 text-warning-600" data-id="element-1572" />
                  </div>
                  <span className="text-body-sm font-medium text-surface-600" data-id="element-1573">
                    No Shows
                  </span>
                </div>
                <p className="text-display-sm font-bold text-surface-900" data-id="element-1574">
                  {selectedAirline.noShowCount.toLocaleString()}
                </p>
                <p className="text-caption text-surface-500 mt-1" data-id="element-1575">
                  Passengers who missed flights
                </p>
              </div>

              {/* Refunds Processed */}
              <div className="p-4 bg-white border border-surface-200 rounded-xl shadow-sm" data-id="element-1576">
                <div className="flex items-center gap-2 mb-2" data-id="element-1577">
                  <div className="p-1.5 bg-primary-100 rounded-lg" data-id="element-1578">
                    <FileTextIcon className="w-4 h-4 text-primary-600" data-id="element-1579" />
                  </div>
                  <span className="text-body-sm font-medium text-surface-600" data-id="element-1580">
                    Refunds Processed
                  </span>
                </div>
                <p className="text-display-sm font-bold text-surface-900" data-id="element-1581">
                  {selectedAirline.refundsProcessedCount.toLocaleString()}
                </p>
                <p className="text-caption text-surface-500 mt-1" data-id="element-1582">
                  Successful refund claims
                </p>
              </div>

              {/* Total Refund Value */}
              <div className="p-4 bg-white border border-surface-200 rounded-xl shadow-sm" data-id="element-1583">
                <div className="flex items-center gap-2 mb-2" data-id="element-1584">
                  <div className="p-1.5 bg-success-100 rounded-lg" data-id="element-1585">
                    <DollarSignIcon className="w-4 h-4 text-success-600" data-id="element-1586" />
                  </div>
                  <span className="text-body-sm font-medium text-surface-600" data-id="element-1587">
                    Total Refund Value
                  </span>
                </div>
                <p className="text-heading-lg font-bold text-surface-900" data-id="element-1588">
                  {formatNaira(selectedAirline.totalRefundValue, {
                compact: true
              })}
                </p>
                <p className="text-caption text-surface-500 mt-1" data-id="element-1589">
                  Total value of processed refunds
                </p>
              </div>

              {/* Amount Paid Back */}
              <div className="p-4 bg-white border border-surface-200 rounded-xl shadow-sm" data-id="element-1590">
                <div className="flex items-center gap-2 mb-2" data-id="element-1591">
                  <div className="p-1.5 bg-accent-100 rounded-lg" data-id="element-1592">
                    <TrendingUpIcon className="w-4 h-4 text-accent-600" data-id="element-1593" />
                  </div>
                  <span className="text-body-sm font-medium text-surface-600" data-id="element-1594">
                    Amount Paid Back
                  </span>
                </div>
                <p className="text-heading-lg font-bold text-surface-900" data-id="element-1595">
                  {formatNaira(selectedAirline.amountPaidBack, {
                compact: true
              })}
                </p>
                <p className="text-caption text-surface-500 mt-1" data-id="element-1596">
                  Total amount redeemed by airline
                </p>
              </div>
            </div>

            {/* Outstanding Balance */}
            <div className="p-6 bg-surface-900 rounded-xl text-white" data-id="element-1597">
              <div className="flex items-center justify-between mb-2" data-id="element-1598">
                <span className="text-body-md font-medium text-surface-300" data-id="element-1599">
                  Outstanding Balance (Amount Owed)
                </span>
                <StatusBadge status={selectedAirline.amountOwed > 0 ? 'failed' : 'complete'} size="sm" data-id="element-1600">
                  {selectedAirline.amountOwed > 0 ? 'Outstanding' : 'Settled'}
                </StatusBadge>
              </div>
              <div className="flex items-end gap-2" data-id="element-1601">
                <span className="text-display-md font-bold" data-id="element-1602">
                  {formatNaira(selectedAirline.amountOwed)}
                </span>
              </div>
              <div className="mt-4 h-2 bg-surface-700 rounded-full overflow-hidden" data-id="element-1603">
                <div className="h-full bg-primary-500 rounded-full" style={{
              width: `${selectedAirline.amountPaidBack / selectedAirline.totalRefundValue * 100}%`
            }} data-id="element-1604" />
              </div>
              <div className="flex justify-between mt-2 text-caption text-surface-400" data-id="element-1605">
                <span data-id="element-1606">
                  Paid:{' '}
                  {Math.round(selectedAirline.amountPaidBack / selectedAirline.totalRefundValue * 100)}
                  %
                </span>
                <span data-id="element-1607">
                  Total:{' '}
                  {formatNaira(selectedAirline.totalRefundValue, {
                compact: true
              })}
                </span>
              </div>
            </div>
          </div>}
        <ModalFooter data-id="element-1608">
          <Button variant="secondary" onClick={() => setSelectedAirline(null)} data-id="element-1609">
            Close
          </Button>
          <Button icon={<FileTextIcon className="w-4 h-4" data-id="element-1611" />} data-id="element-1610">
            Download Report
          </Button>
        </ModalFooter>
      </Modal>
    </div>;
}